import { graphql, type HeadProps, type PageProps } from 'gatsby'
import React from 'react'

import Newsletter from '@/components/centered-call-to-action/centered-call-to-action'
import HeaderPerson from '@/components/models/person/header-person/header-person'
import MainPerson from '@/components/models/person/main-person/main-person'
import Related from '@/components/related/related'
import { Microdata } from '@/components/seo/microdata'
import Seo from '@/components/seo/seo'
import Layout from '@/layouts/layout'
import { sortRelatedContent } from '@/utils/sort'

export type DataProps = {
  datoCmsGctPerson: Queries.PersonQuery['datoCmsGctPerson']
  allDatoCmsPctPost: Queries.PostQuery['allDatoCmsPctPost']
  allDatoCmsPctInterview: Queries.PostQuery['allDatoCmsPctInterview']
  allDatoCmsPctPaper: Queries.PostQuery['allDatoCmsPctPaper']
  allDatoCmsPctDialog: Queries.PostQuery['allDatoCmsPctDialog']
  allDatoCmsPctProject: Queries.PostQuery['allDatoCmsPctProject']
  allDatoCmsPctEpisode: Queries.PostQuery['allDatoCmsPctEpisode']
  allDatoCmsPctExternalContent: Queries.PostQuery['allDatoCmsPctExternalContent']
  allDatoCmsPctEvent: Queries.PostQuery['allDatoCmsPctEvent']
}

export const Head: React.FC<HeadProps<DataProps>> = ({
  data: { datoCmsGctPerson },
  location
}) => {
  return (
    <>
      <Seo
        data={datoCmsGctPerson}
        title={`${datoCmsGctPerson?.firstName} ${datoCmsGctPerson?.secondName}`}
        description={datoCmsGctPerson?.bio}
        location={location}
      />
      <Microdata data={datoCmsGctPerson} location={location} />
    </>
  )
}

const Person: React.FC<PageProps<DataProps>> = ({
  data: {
    datoCmsGctPerson,
    allDatoCmsPctPost,
    allDatoCmsPctInterview,
    allDatoCmsPctPaper,
    allDatoCmsPctDialog,
    allDatoCmsPctProject,
    allDatoCmsPctEpisode,
    allDatoCmsPctExternalContent,
    allDatoCmsPctEvent
  },
  location
}) => {
  if (datoCmsGctPerson === null) {
    return <div>Not found</div>
  }
  const related = sortRelatedContent({
    nodesGroups: [
      [
        ...(allDatoCmsPctPost.nodes ?? []),
        ...(allDatoCmsPctInterview.nodes ?? []),
        ...(allDatoCmsPctProject.nodes ?? []),
        ...(allDatoCmsPctPaper.nodes ?? []),
        ...(allDatoCmsPctDialog.nodes ?? []),
        ...(allDatoCmsPctEpisode.nodes ?? []),
        ...(allDatoCmsPctExternalContent.nodes ?? []),
        ...(allDatoCmsPctEvent.nodes ?? [])
      ]
    ]
  })

  return (
    <Layout location={location} showNavigation>
      <article>
        <HeaderPerson {...datoCmsGctPerson} />
        <MainPerson {...datoCmsGctPerson} />
        <Related
          title={`Authored by ${datoCmsGctPerson.firstName} ${datoCmsGctPerson.secondName}`}
          related={related}
        />
      </article>
      <Newsletter />
    </Layout>
  )
}

export default Person

export const query = graphql`
  query Person($id: String, $related: [String] = "") {
    datoCmsGctPerson(id: { eq: $id }) {
      ...GctPerson
      seoMetaTags {
        ...GatsbyDatoCmsSeoMetaTags
      }
    }
    allDatoCmsPctPost(
      sort: { meta: { firstPublishedAt: DESC } }
      filter: { id: { in: $related } }
    ) {
      nodes {
        ...PctPostCard
      }
    }
    allDatoCmsPctInterview(
      sort: { meta: { firstPublishedAt: DESC } }
      filter: { id: { in: $related } }
    ) {
      nodes {
        ...PctInterviewCard
      }
    }
    allDatoCmsPctPaper(
      sort: { meta: { firstPublishedAt: DESC } }
      filter: { id: { in: $related } }
    ) {
      nodes {
        ...PctPaperCard
      }
    }
    allDatoCmsPctDialog(
      sort: { meta: { firstPublishedAt: DESC } }
      filter: { id: { in: $related } }
    ) {
      nodes {
        ...PctDialogCard
      }
    }
    allDatoCmsPctProject(
      sort: { meta: { firstPublishedAt: DESC } }
      filter: { id: { in: $related } }
    ) {
      nodes {
        ...PctProjectCard
      }
    }
    allDatoCmsPctEpisode(
      sort: { meta: { firstPublishedAt: DESC } }
      filter: { id: { in: $related } }
    ) {
      nodes {
        ...PctEpisodeCard
      }
    }
    allDatoCmsPctExternalContent(
      sort: { meta: { firstPublishedAt: DESC } }
      filter: { showInWebsite: { eq: true }, id: { in: $related } }
    ) {
      nodes {
        ...PctExternalContentCard
      }
    }
    allDatoCmsPctEvent(
      sort: { meta: { firstPublishedAt: DESC } }
      filter: { id: { in: $related } }
    ) {
      nodes {
        ...PctEventCard
      }
    }
  }

  fragment PersonReference on DatoCmsGctPerson {
    __typename
    firstName
    secondName
    job
  }

  fragment GctPerson on DatoCmsGctPerson {
    __typename
    originalId
    id
    meta {
      firstPublishedAt
      publishedAtTimestamp: publishedAt(formatString: "X")
    }
    seo {
      title
      description
      image {
        url
      }
    }
    firstName
    secondName
    bio
    job
    linkedin
    about {
      value
    }
    expertise {
      __typename
      ... on DatoCmsPctTag {
        slug
        externalName
      }
      ... on DatoCmsPctTopic {
        slug
        externalName
      }
    }
    company {
      externalName
      logo {
        url
        gatsbyImageData
      }
      url
    }
    card: photo {
      ...imgCard
    }
    photo {
      alt
      url
      gatsbyImageData(
        width: 600
        height: 700
        imgixParams: { w: "600", h: "700", fit: "crop", crop: "focalpoint" }
      )
    }
  }
`
